import type { SesionIniciada } from './tipos'

const BASE = '/api/v1'
const CLAVE_SESION = 'minimarket.sesion'

/** Error con el codigo estable que devuelve el backend en ProblemDetails. */
export class ErrorApi extends Error {
    constructor(
        mensaje: string,
        readonly estado: number,
        readonly codigo: string,
    ) {
        super(mensaje)
        this.name = 'ErrorApi'
    }

    /** El token vencio o la cuenta perdio acceso: hay que volver a entrar. */
    get esSesionInvalida() {
        return this.estado === 401
    }

    get esPermisoDenegado() {
        return this.estado === 403 && this.codigo !== 'autorizacion_requerida'
    }

    /**
     * Texto para mostrar al usuario. El backend ya devuelve mensajes redactados
     * para leerse tal cual; solo se reemplazan los que no dicen nada util.
     */
    mensajeVisible(): string {
        if (this.estado === 0 || this.estado >= 500) {
            return 'El servidor no respondió. Intente de nuevo en unos segundos.'
        }

        if (this.codigo === 'desconocido') {
            return 'No se pudo completar la operación.'
        }

        return this.message
    }
}

interface SesionGuardada {
    tokenAcceso: string
    tokenRefresco: string
    expiraEn: string
}

let sesion: SesionGuardada | null = leerSesion()
let alPerderSesion: (() => void) | null = null

function leerSesion(): SesionGuardada | null {
    try {
        const guardado = localStorage.getItem(CLAVE_SESION)
        return guardado ? (JSON.parse(guardado) as SesionGuardada) : null
    } catch {
        // Modo privado o almacenamiento bloqueado: se opera sin recordar sesion.
        return null
    }
}

export function guardarSesion(datos: SesionIniciada) {
    sesion = {
        tokenAcceso: datos.tokenAcceso,
        tokenRefresco: datos.tokenRefresco,
        expiraEn: datos.expiraEn,
    }

    try {
        localStorage.setItem(CLAVE_SESION, JSON.stringify(sesion))
    } catch {
        // Sin persistencia la sesion dura lo que dure la pestania.
    }
}

export function borrarSesion() {
    sesion = null
    try {
        localStorage.removeItem(CLAVE_SESION)
    } catch {
        /* nada que hacer */
    }
}

export function haySesion() {
    return sesion !== null
}

export function tokenRefrescoActual() {
    return sesion?.tokenRefresco ?? null
}

/** Se avisa a la aplicacion cuando la sesion deja de servir, para volver al login. */
export function alCaducarSesion(manejador: () => void) {
    alPerderSesion = manejador
}

/**
 * Renueva el token de acceso. Se guarda la promesa en curso para que varias
 * peticiones que caducan a la vez no disparen varios refrescos: el backend rota
 * el token en cada uso y el segundo refresco anularia toda la cadena por
 * considerarlo reutilizacion.
 */
let refrescoEnCurso: Promise<boolean> | null = null

async function refrescar(): Promise<boolean> {
    if (!sesion?.tokenRefresco) return false

    if (!refrescoEnCurso) {
        refrescoEnCurso = (async () => {
            try {
                const respuesta = await fetch(`${BASE}/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tokenRefresco: sesion?.tokenRefresco }),
                })

                if (!respuesta.ok) return false

                guardarSesion((await respuesta.json()) as SesionIniciada)
                return true
            } catch {
                return false
            } finally {
                // Se libera en el microtask siguiente para que las peticiones
                // que esperaban lean ya el token nuevo.
                queueMicrotask(() => {
                    refrescoEnCurso = null
                })
            }
        })()
    }

    return refrescoEnCurso
}

interface Opciones {
    metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    cuerpo?: unknown
    /** Carga de archivos. Excluyente con `cuerpo`. */
    formulario?: FormData
    parametros?: Record<string, string | number | boolean | null | undefined>
    senal?: AbortSignal
}

async function ejecutar(ruta: string, opciones: Opciones, reintentar: boolean): Promise<Response> {
    const url = new URL(`${BASE}${ruta}`, window.location.origin)

    for (const [clave, valor] of Object.entries(opciones.parametros ?? {})) {
        if (valor !== null && valor !== undefined && valor !== '') {
            url.searchParams.set(clave, String(valor))
        }
    }

    const cabeceras: Record<string, string> = {}

    if (sesion?.tokenAcceso) {
        cabeceras.Authorization = `Bearer ${sesion.tokenAcceso}`
    }

    // Con FormData no se fija Content-Type a mano: lo tiene que poner el
    // navegador, porque incluye el 'boundary' que separa las partes.
    if (opciones.cuerpo !== undefined) {
        cabeceras['Content-Type'] = 'application/json'
    }

    const respuesta = await fetch(url, {
        method: opciones.metodo ?? 'GET',
        headers: cabeceras,
        body:
            opciones.formulario ??
            (opciones.cuerpo === undefined ? undefined : JSON.stringify(opciones.cuerpo)),
        signal: opciones.senal,
    })

    // Un solo reintento: si el refresco tampoco sirve, la sesion se acabo.
    if (respuesta.status === 401 && reintentar && (await refrescar())) {
        return ejecutar(ruta, opciones, false)
    }

    return respuesta
}

async function traducirError(respuesta: Response): Promise<ErrorApi> {
    let mensaje = `Error ${respuesta.status}`
    let codigo = 'desconocido'

    try {
        const problema = (await respuesta.json()) as {
            detail?: string
            title?: string
            codigo?: string
            errors?: Record<string, string[]>
        }

        // Los errores de validacion del modelo vienen en 'errors'; se muestra
        // el primero, que es el que el usuario tiene delante.
        const primerError = problema.errors
            ? Object.values(problema.errors).flat()[0]
            : undefined

        mensaje = primerError ?? problema.detail ?? problema.title ?? mensaje
        codigo = problema.codigo ?? codigo
    } catch {
        // Respuesta sin cuerpo JSON: se queda el mensaje generico.
    }

    return new ErrorApi(mensaje, respuesta.status, codigo)
}

export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
    const respuesta = await ejecutar(ruta, opciones, true)

    if (!respuesta.ok) {
        const error = await traducirError(respuesta)

        if (error.esSesionInvalida) {
            borrarSesion()
            alPerderSesion?.()
        }

        throw error
    }

    if (respuesta.status === 204 || respuesta.headers.get('Content-Length') === '0') {
        return undefined as T
    }

    return (await respuesta.json()) as T
}

/**
 * Descarga un archivo del backend. No sirve un enlace normal: los endpoints
 * exigen la cabecera Authorization, que un <a href> no manda.
 */
export async function descargar(
    ruta: string,
    nombreArchivo: string,
    parametros?: Opciones['parametros'],
): Promise<void> {
    const respuesta = await ejecutar(ruta, { metodo: 'GET', parametros }, true)

    if (!respuesta.ok) {
        const error = await traducirError(respuesta)

        if (error.esSesionInvalida) {
            borrarSesion()
            alPerderSesion?.()
        }

        throw error
    }

    const url = URL.createObjectURL(await respuesta.blob())
    const enlace = document.createElement('a')

    enlace.href = url
    enlace.download = nombreArchivo
    document.body.appendChild(enlace)
    enlace.click()
    enlace.remove()

    // Se libera despues: revocar en el mismo tick corta la descarga en algunos
    // navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const api = {
    get: <T>(ruta: string, parametros?: Opciones['parametros'], senal?: AbortSignal) =>
        pedir<T>(ruta, { metodo: 'GET', parametros, senal }),

    post: <T>(ruta: string, cuerpo?: unknown) => pedir<T>(ruta, { metodo: 'POST', cuerpo }),

    put: <T>(ruta: string, cuerpo?: unknown) => pedir<T>(ruta, { metodo: 'PUT', cuerpo }),

    delete: <T>(ruta: string) => pedir<T>(ruta, { metodo: 'DELETE' }),

    /**
     * Sube un archivo como multipart. El nombre del campo tiene que coincidir
     * con el del parametro IFormFile del controlador.
     */
    subirArchivo: <T>(ruta: string, archivo: File, campo = 'archivo') => {
        const formulario = new FormData()
        formulario.append(campo, archivo)

        return pedir<T>(ruta, { metodo: 'POST', formulario })
    },
}
